import { useState, useMemo, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  getAttributes,
  getColumnAttributes,
  selectedPolicyEntitiesFormData
} from '../../../store/role/policyEntity/entitySelector';
import { IAttribute, IPolicyRuleData } from '../../../models/Policies/PolicyContract';
import { policyEntityReducerInitialState } from '../../../store/role/policyEntity/entityReducer';
import { setSnackBarMessage } from '../../../store/snackBar/snackBarActionCreator';
import { SNACKBAR_ALERT_DEFAULT_PROPS } from '../../../components/RoleDetails/constants';
import { getEntityPolicyExpression, validateEntityForm } from '../PolicyUtils';

export default function useEntityPolicyForm() {
  const entitiesFormData = useSelector(selectedPolicyEntitiesFormData);
  const attributes = useSelector(getAttributes);
  const columnAttributes = useSelector(getColumnAttributes);
  const dispatch = useDispatch();

  const areAttributesValid = useMemo(
    () =>
      !(
        attributes.some(
          (attr: IAttribute) =>
            attr.attribute.key === 'select' || !attr.value || (attr.valueAction && attr.valueAction.length === 0)
        ) ||
        columnAttributes.some(
          (attr: IAttribute) =>
            attr.attribute.key === 'select' || (attr.attributeAction && attr.attributeAction.length === 0)
        )
      ),
    [columnAttributes, attributes]
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitEntityForm = useCallback(
    (createAndAssignPolicyToRole: (newPolicyData: IPolicyRuleData) => void) => {
      if (
        validateEntityForm(entitiesFormData, dispatch, policyEntityReducerInitialState.entityFormError) &&
        (entitiesFormData.policyResource !== 'location' || areAttributesValid)
      ) {
        dispatch(
          setSnackBarMessage({
            ...SNACKBAR_ALERT_DEFAULT_PROPS,
            showSnackbar: false
          })
        );
        setIsSubmitting(true);
        const policyExpression = getEntityPolicyExpression(entitiesFormData, columnAttributes, attributes);
        const entityPolicy = {
          displayName: entitiesFormData.policyName,
          description: entitiesFormData.policyDescription,
          expression: policyExpression
        };
        createAndAssignPolicyToRole(entityPolicy);
      }
    },
    [entitiesFormData, columnAttributes, attributes, areAttributesValid, dispatch]
  );

  return {
    isEntitySaveEnabled: Boolean(
      entitiesFormData.policyName !== '' &&
      entitiesFormData.policyDescription !== '' &&
      entitiesFormData.policyResource !== '' &&
      entitiesFormData.policyNamespace !== '' &&
      entitiesFormData.policyAction.length !== 0 &&
      attributes &&
      areAttributesValid &&
      !isSubmitting
    ),
    submitEntityForm
  };
}
